// NOTE: IF YOU CHANGE THE CONTENT HERE YOU NEED TO CHANGE THE CONTENT ON THE WRAPPED CALCULATOR HERE AND ON WEBSITE TOO

import {
  Button,
  Divider,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  Switch,
  Tooltip,
} from '@nextui-org/react';
import {
  Clipboard,
  Percent,
  PlusCircle,
  Question,
  User,
  X,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Chart from 'react-google-charts';
import toast, { Toaster } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import posthog from 'posthog-js';

import '@fontsource/pathway-gothic-one/index.css';
import '@fontsource-variable/inter/index.css';
import '@fontsource/ia-writer-mono';

export type Tier = {
  id: string;
  pledgers: number;
  amount: number;
  label?: string;
};

export type CalculatorProps = {};

const membershipsRake = 0;

const currencyTypes = [
  { name: 'DKK', label: 'DKK', value: 'da-DK', currency: 'DKK', symbol: 'kr' },
  { name: 'USD', label: 'USD', value: 'en-US', currency: 'USD', symbol: '$' },
  { name: 'EUR', label: 'EUR', value: 'en-GB', currency: 'EUR', symbol: '€' },
];

if (typeof window !== 'undefined' && !posthog.__loaded) {
  posthog.init('phc_aDiLXpzc4PfexVbJojOhrEDrsoQ6OFzqA6okexm9qSR', {
    api_host: 'https://sleeve.fm/s/m',
    person_profiles: 'always',
    autocapture: false,
  });
}

export const Calculator: React.FC<CalculatorProps> = () => {
  const [socialFollowers, setSocialFollowers] = useState(100000);
  const [socialPercent, setSocialPercent] = useState(10);
  const [emailSubscribers, setEmailSubscribers] = useState(20000);
  const [hasEditedTiers, setHasEditedTiers] = useState(false);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [currencyName, setCurrencyName] = useState<'USD' | 'DKK' | 'EUR'>(
    'USD'
  );
  const [frequency, setFrequency] = useState<'monthly' | 'yearly'>('yearly');
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(true);
  const [hasLoadedTiersFromUrl, setHasLoadedTiersFromUrl] = useState(false);

  const frequencyMultiplier = frequency === 'monthly' ? 1 : 12;
  const currencies = {
    USD: { symbol: '$', locale: 'en-US', fee: 0.3 },
    DKK: { symbol: 'kr', locale: 'da-DK', fee: 1.8 },
    EUR: { symbol: '€', locale: 'en-GB', fee: 0.3 },
  };
  const currentCurrency = currencies[currencyName];

  const freeTierTotal = useMemo(
    () => emailSubscribers + (socialPercent / 100) * socialFollowers,
    [emailSubscribers, socialFollowers, socialPercent]
  );

  // Fire once when the user first models their numbers (edits a tier or input)
  const hasTrackedModeled = useRef(false);
  const trackModeled = useCallback(() => {
    if (!hasTrackedModeled.current) {
      hasTrackedModeled.current = true;
      posthog.capture('calculator_modeled');
    }
  }, []);

  // Decode state from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedData = Array.from(urlParams.keys())[0];

    if (encodedData) {
      try {
        const decodedData = JSON.parse(atob(decodeURIComponent(encodedData)));
        console.log('Decoded data:', decodedData);

        // Set all state at once to avoid race conditions
        if (decodedData.socialFollowers !== undefined) {
          setSocialFollowers(decodedData.socialFollowers);
        }
        if (decodedData.socialPercent !== undefined) {
          setSocialPercent(decodedData.socialPercent);
        }
        if (decodedData.emailSubscribers !== undefined) {
          setEmailSubscribers(decodedData.emailSubscribers);
        }
        if (decodedData.currencyName) {
          setCurrencyName(decodedData.currencyName);
        }
        if (decodedData.frequency) {
          setFrequency(decodedData.frequency);
        }
        if (decodedData.tiers && Array.isArray(decodedData.tiers)) {
          setTiers(decodedData.tiers);
          setHasLoadedTiersFromUrl(true);
        }

        setIsLoadingFromUrl(false);
      } catch (error) {
        console.error('Failed to decode URL data:', error);
        setIsLoadingFromUrl(false);
      }
    } else {
      setIsLoadingFromUrl(false);
    }
  }, []);

  // Initialize default tiers only if not loading from URL and tiers are empty
  useEffect(() => {
    if (!isLoadingFromUrl && tiers.length === 0) {
      console.log('Initializing default tiers');
      setTiers([
        {
          id: 'free',
          pledgers: Math.round(freeTierTotal),
          amount: 0,
          label: 'Free',
        },
        {
          id: 'base',
          pledgers: Math.round(freeTierTotal * 0.1),
          amount: 5,
          label: 'Base',
        },
        {
          id: 'premium',
          pledgers: Math.round(freeTierTotal * 0.01),
          amount: 10,
          label: 'Premium',
        },
        {
          id: 'VIP',
          pledgers: Math.round(freeTierTotal * 0.0025),
          amount: 50,
          label: 'VIP',
        },
      ]);
    }
  }, [freeTierTotal, isLoadingFromUrl, tiers.length]);

  // Update tiers when audience changes - robust logic
  useEffect(() => {
    if (isLoadingFromUrl || tiers.length === 0) return;

    console.log('Audience change effect triggered', {
      freeTierTotal,
      hasEditedTiers,
      hasLoadedTiersFromUrl,
      tiersLength: tiers.length,
    });

    if (!hasEditedTiers && !hasLoadedTiersFromUrl) {
      // Fresh state or after reset - update all tiers based on audience
      console.log(
        'Updating all tiers based on audience change (fresh/reset state)'
      );
      setTiers([
        {
          id: 'free',
          pledgers: Math.round(freeTierTotal),
          amount: 0,
          label: 'Free',
        },
        {
          id: 'base',
          pledgers: Math.round(freeTierTotal * 0.1),
          amount: 5,
          label: 'Base',
        },
        {
          id: 'premium',
          pledgers: Math.round(freeTierTotal * 0.01),
          amount: 10,
          label: 'Premium',
        },
        {
          id: 'VIP',
          pledgers: Math.round(freeTierTotal * 0.0025),
          amount: 50,
          label: 'VIP',
        },
      ]);
    } else {
      // We have custom tiers (from URL or manual edits) - only update free tier
      console.log('Updating only free tier (preserving custom values)');
      setTiers((currentTiers) =>
        currentTiers.map((tier) =>
          tier.id === 'free'
            ? { ...tier, pledgers: Math.round(freeTierTotal) }
            : tier
        )
      );
    }
  }, [freeTierTotal, isLoadingFromUrl, hasEditedTiers, hasLoadedTiersFromUrl]);

  // Encode state into URL whenever state changes (but not during initial load)
  useEffect(() => {
    if (isLoadingFromUrl || tiers.length === 0) return;

    const data = btoa(
      JSON.stringify({
        socialFollowers,
        socialPercent,
        emailSubscribers,
        tiers,
        currencyName,
        frequency,
      })
    );
    const newurl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      '?' +
      encodeURIComponent(data);
    window.history.replaceState({ path: newurl }, '', newurl);
    console.log('Encoded state to URL');
  }, [
    socialFollowers,
    socialPercent,
    emailSubscribers,
    tiers,
    currencyName,
    frequency,
    isLoadingFromUrl,
  ]);

  const updateTierPledgers = useCallback(
    (id: string, pledgers: number) => {
      trackModeled();
      setHasEditedTiers(true);
      setTiers((tiers) =>
        tiers.map((t) => (t.id === id ? { ...t, pledgers } : t))
      );
    },
    [trackModeled]
  );

  const updateTierAmount = useCallback(
    (id: string, amount: number) => {
      trackModeled();
      setHasEditedTiers(true);
      setTiers((tiers) =>
        tiers.map((t) => (t.id === id ? { ...t, amount } : t))
      );
    },
    [trackModeled]
  );

  const deleteTier = (id: string) => {
    setHasEditedTiers(true);
    setTiers((tiers) => tiers.filter((t) => t.id !== id));
  };

  const addTier = () => {
    setHasEditedTiers(true);
    setTiers((tiers) => [...tiers, { id: uuidv4(), pledgers: 10, amount: 7 }]);
  };

  const calculateStripeFee = (amount: number) =>
    amount <= 0 ? 0 : amount * 0.03 + currencies[currencyName].fee;
  const tierIncome = (tier: Tier) =>
    tier.amount > 0
      ? (tier.amount -
          tier.amount * membershipsRake -
          calculateStripeFee(tier.amount)) *
        tier.pledgers
      : 0;
  const pledgeIncome =
    tiers.reduce((acc, t) => acc + tierIncome(t), 0) * frequencyMultiplier;

  const currencyFormatter = new Intl.NumberFormat(currentCurrency.locale, {
    style: 'currency',
    currency: currencyName,
    maximumFractionDigits: 0,
  });

  const sankeyOptions = useMemo(
    () => ({
      legend: 'none',
      animation: {
        duration: 1000,
        easing: 'out',
        startup: true,
      },
      sankey: {
        node: {
          label: {
            fontSize: 14,
            bold: true,
          },
        },
      },
      tooltip: {
        isHtml: true,
        textStyle: {
          fontSize: 14,
          fontName: 'Inter, system-ui, sans-serif',
        },
      },
    }),
    []
  );

  const sankeyData = useMemo(() => {
    const totals = {
      you: 0,
      processing: 0,
    };

    tiers.forEach((tier) => {
      const amount = Number(tier.amount);
      const pledgers = Number(tier.pledgers);
      if (isNaN(amount) || isNaN(pledgers) || amount <= 0 || pledgers <= 0)
        return;
      totals.you += tierIncome(tier) * frequencyMultiplier;
      totals.processing +=
        calculateStripeFee(amount) * pledgers * frequencyMultiplier;
    });

    return [
      [
        'From',
        'To',
        'Amount',
        { role: 'tooltip', type: 'string', p: { html: true } },
      ],
      [
        'Members',
        'You',
        Math.round(totals.you),
        `<div style="padding: 8px; font-family: Inter, system-ui, sans-serif;"><strong>${currencyFormatter.format(
          Math.round(totals.you)
        )}</strong></div>`,
      ],
      [
        'Members',
        'Processing Fees',
        Math.round(totals.processing),
        `<div style="padding: 8px; font-family: Inter, system-ui, sans-serif;"><strong>${currencyFormatter.format(
          Math.round(totals.processing)
        )}</strong></div>`,
      ],
    ];
  }, [tiers, frequencyMultiplier, currencyFormatter]);

  return (
    <div className="calculator space-y-6">
      {/* intro copy and currency section  */}
      <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-2">
        <p className="prose prose-sm text-balance italic">
          Try modeling your tiers below and start imagining what a direct
          connection with your audience could mean for your music and your
          future.
        </p>
        <div className="flex justify-end gap-2 w-full sm:w-auto">
          <div>
            <Tooltip
              content="Click to copy a link to this model"
              placement="left"
              radius="none"
            >
              <Button
                isIconOnly
                variant="flat"
                radius="none"
                size="sm"
                className="min-h-[44px] min-w-[44px]"
                onClick={() => {
                  posthog.capture('CalculatorShare');
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Copied to clipboard');
                }}
              >
                <Clipboard size={18} />
              </Button>
            </Tooltip>
            <Toaster
              toastOptions={{
                duration: 3000,
              }}
              containerClassName="font-inter"
            />
          </div>
          <Select
            size="sm"
            radius="none"
            variant="bordered"
            classNames={{
              base: 'w-24 base',
              trigger: 'shadow-none min-h-[44px]',
              mainWrapper: 'mainwrapper',
              innerWrapper: 'innerwrapper',
              label: 'label',
            }}
            id="currency"
            selectedKeys={[currencyName]}
            onSelectionChange={(e) =>
              setCurrencyName(Array.from(e)[0] as 'USD' | 'DKK' | 'EUR')
            }
          >
            {currencyTypes.map((d) => (
              <SelectItem key={d.currency} id="currency">
                {d.name}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>
      <Divider />
      {/* income section  */}
      <div className="sticky top-4 z-10 bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm grid gap-4">
        <div>
          <h3 className="prose-md sm:prose-lg font-semibold">
            Estimated Income
          </h3>
          <div className="flex flex-row items-center justify-between gap-2 sm:gap-0">
            <p className="text-xl sm:text-2xl font-bold">
              {currencyFormatter.format(pledgeIncome)}
            </p>
            <Switch
              classNames={{
                label: 'min-w-16',
              }}
              isSelected={frequency === 'yearly'}
              onValueChange={(val) => setFrequency(val ? 'yearly' : 'monthly')}
            >
              {frequency === 'monthly' ? 'Monthly' : 'Yearly'}
            </Switch>
          </div>
        </div>
        <Divider />
        {/* audience section */}
        <div className="flex flex-col gap-2">
          <Popover
            placement="bottom"
            showArrow={true}
            triggerScaleOnOpen={false}
          >
            <PopoverTrigger>
              <h3 className="prose-md font-semibold flex items-center hover:cursor-pointer gap-1">
                <span>Your Audience</span> <Question size={18} weight="fill" />
              </h3>
            </PopoverTrigger>
            <PopoverContent>
              <div className="px-1 py-2">
                <p className="text-sm font-bold">
                  Add your email list size and your total social followers.
                </p>
                <p className="text-sm text-gray-500">
                  We'll estimate how many might join you on Sleeve. We start
                  with your email list plus 10% of social followers as your free
                  tier, but your fans might surprise you! From there, we
                  estimate 10% might become paying members and 1% premium
                  supporters.
                </p>
                <p className="text-sm text-gray-500">
                  We&apos;ll use this to model your potential subscriber base
                  below.
                </p>
              </div>
            </PopoverContent>
          </Popover>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-2">
            {/* Email List Size - Full width on mobile, 1/3 on desktop */}
            <Input
              variant="faded"
              type="number"
              placeholder="0.00"
              labelPlacement="outside"
              radius="none"
              endContent={
                <div className="pointer-events-none flex items-center">
                  <User size={14} />
                </div>
              }
              classNames={{
                mainWrapper: 'mainwrapper',
                inputWrapper: 'shadow-none !ps-0 inputwrapper min-h-[44px]',
                innerWrapper: 'innerwrapper',
                input:
                  'group-data-[focus=true]/text:!outline-none group-data-[focus=true]/text:!border-none !outline-none !border-none focus:ring-0 input text-base',
              }}
              min={0}
              value={String(emailSubscribers)}
              onChange={(e) => {
                trackModeled();
                setEmailSubscribers(Number(e.target.value));
              }}
              label="Email List Size"
            />

            {/* Social Followers - Takes most space on mobile second row */}
            <div className="grid grid-cols-[1fr_120px] sm:contents gap-3 sm:gap-4">
              <Input
                variant="faded"
                type="number"
                placeholder="0.00"
                labelPlacement="outside"
                radius="none"
                endContent={
                  <div className="pointer-events-none flex items-center">
                    <User size={14} />
                  </div>
                }
                classNames={{
                  mainWrapper: 'mainwrapper',
                  inputWrapper: 'shadow-none !ps-0 inputwrapper min-h-[44px]',
                  innerWrapper: 'innerwrapper',
                  input:
                    'group-data-[focus=true]/text:!outline-none group-data-[focus=true]/text:!border-none !outline-none !border-none focus:ring-0 input text-base',
                }}
                min={0}
                value={String(socialFollowers)}
                label="Total Social Followers"
                onChange={(e) => {
                  trackModeled();
                  setSocialFollowers(Number(e.target.value));
                }}
              />

              {/* % on Sleeve - Small width for 3 characters */}
              <Input
                variant="faded"
                type="number"
                placeholder="0"
                labelPlacement="outside"
                radius="none"
                label="% on Sleeve"
                endContent={
                  <div className="pointer-events-none flex items-center">
                    <Percent size={14} />
                  </div>
                }
                classNames={{
                  mainWrapper: 'mainwrapper',
                  inputWrapper: 'shadow-none !ps-0 inputwrapper min-h-[44px]',
                  innerWrapper: 'innerwrapper',
                  input:
                    'group-data-[focus=true]/text:!outline-none group-data-[focus=true]/text:!border-none !outline-none !border-none focus:ring-0 input text-base text-center sm:text-left',
                }}
                min={0}
                max={100}
                value={String(socialPercent)}
                onChange={(e) => {
                  trackModeled();
                  setSocialPercent(Number(e.target.value));
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* chart section  */}
      <div className="px-1 sm:px-2">
        <Chart
          chartType="Sankey"
          width="100%"
          height="250px"
          data={sankeyData}
          options={sankeyOptions}
        />
      </div>

      <Divider />

      {/* membership tiers section  */}
      <div className="px-1 sm:px-2">
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Membership Tiers
        </h2>

        {tiers.map((t, index) => (
          <div key={t.id} className="my-2">
            {index === 0 && (
              <h3 className="text-sm font-semibold text-gray-900">
                Free Tier/Followers ({emailSubscribers.toLocaleString()} from
                email list + {socialPercent}% of{' '}
                {socialFollowers.toLocaleString()} social)
              </h3>
            )}
            {index === 1 && (
              <h3 className="mt-6 text-sm font-semibold text-gray-900">
                Paid Tiers
              </h3>
            )}
            {index === 1 && (
              <div className="mt-2 hidden sm:flex items-center gap-4 text-sm font-medium text-gray-500">
                <span className="w-1/2">Members</span>
                <span className="w-1/2">Tier Price</span>
                <span className="min-w-8"></span>
              </div>
            )}
            <div className="mb-4 flex flex-col gap-1">
              <Divider className="my-2" />
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 order-2 sm:order-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1 sm:hidden">
                    Members
                  </label>
                  <Input
                    variant="faded"
                    type="number"
                    placeholder="0.00"
                    labelPlacement="outside"
                    radius="none"
                    endContent={
                      <div className="pointer-events-none flex items-center">
                        <User size={14} />
                      </div>
                    }
                    classNames={{
                      mainWrapper: 'mainwrapper',
                      inputWrapper:
                        'shadow-none !ps-0 inputwrapper min-h-[44px]',
                      innerWrapper: 'innerwrapper',
                      input:
                        'group-data-[focus=true]/text:!outline-none group-data-[focus=true]/text:!border-none !outline-none !border-none focus:ring-0 input text-base',
                    }}
                    min={0}
                    value={String(t.pledgers)}
                    onChange={(e) =>
                      updateTierPledgers(t.id, e.currentTarget.valueAsNumber)
                    }
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1 sm:hidden">
                    Tier Price
                  </label>
                  <Input
                    variant="faded"
                    type="number"
                    placeholder="0.00"
                    labelPlacement="outside"
                    radius="none"
                    startContent={
                      <div className="pointer-events-none flex items-center">
                        {currentCurrency.symbol}
                      </div>
                    }
                    classNames={{
                      mainWrapper: 'mainwrapper',
                      inputWrapper:
                        'shadow-none !pe-0 inputwrapper min-h-[44px]',
                      innerWrapper: 'innerwrapper',
                      input:
                        'group-data-[focus=true]/text:!outline-none group-data-[focus=true]/text:!border-none !outline-none !border-none focus:ring-0 input text-base',
                    }}
                    min={0}
                    value={String(t.amount)}
                    onChange={(e) =>
                      updateTierAmount(t.id, e.currentTarget.valueAsNumber)
                    }
                    isDisabled={t.id === 'free'}
                  />
                </div>
                <div
                  className={classNames({
                    'justify-center sm:justify-start': true,
                    'sm:invisible hidden': t.id === 'free',
                  })}
                >
                  <Button
                    className={classNames({
                      'flex items-center justify-center sm:!px-0 [&>svg]:!max-w-[32px] [&>svg]:! min-w-[32px]':
                        true,
                      invisible: t.id === 'free',
                    })}
                    size="sm"
                    variant="flat"
                    onClick={() => deleteTier(t.id)}
                  >
                    <span className="sm:hidden">Delete tier</span>
                    <X size={16} />
                  </Button>
                </div>
              </div>
              <p className="sm:pl-2 text-base sm:text-sm text-gray-500 mb-1  mt-2 order-1 sm:order-2">
                <span className="font-semibold">{t.label} Tier:</span>{' '}
                {t.id !== 'free' && tiers[0]?.pledgers > 0
                  ? (() => {
                      const percentage = (t.pledgers / tiers[0].pledgers) * 100;
                      if (percentage < 1 && percentage > 0) {
                        return `${percentage.toFixed(1)}%`;
                      }
                      return `${Math.round(percentage)}%`;
                    })()
                  : ''}{' '}
                {t.id !== 'free' && ' of followers •'}{' '}
                {currencyFormatter.format(tierIncome(t) * frequencyMultiplier)}{' '}
                {frequency}
              </p>
            </div>
          </div>
        ))}

        <div className="flex w-full flex-col gap-3">
          <Divider />
          <button
            onClick={() => addTier()}
            type="button"
            className="inline-flex w-full items-center justify-center gap-x-1.5 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600 shadow-sm hover:bg-gray-100 min-h-[44px]"
          >
            <PlusCircle size={16} />
            Add a Tier
          </button>
          <button
            onClick={() => {
              setHasEditedTiers(false);
              setHasLoadedTiersFromUrl(false);
              // Clear tiers to trigger re-initialization
              setTiers([]);
            }}
            type="button"
            className="inline-flex w-full items-center justify-center gap-x-1.5 px-2 py-1 text-xs font-semibold text-gray-600 min-h-[44px]"
          >
            Reset Tiers to Defaults
          </button>
        </div>
      </div>
    </div>
  );
};
